import { Box, GridLayout, H4, Icon } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecordToArray } from '@deity-io/di-data-basics';
import { Button, Input } from '@deity-io/portal-ui';
import { cloneDeep } from 'lodash';
import type { FC } from 'react';
import { useState } from 'react';
import { isValidFieldName, isValidHeaderName, noWhitespace } from 'utils';

export enum KeyValidators {
  IS_VALID_FIELD_NAME = 'IS_VALID_FIELD_NAME',
  NO_WHITE_SPACE = 'NO_WHITE_SPACE',
  IS_VALID_HEADER_NAME = 'IS_VALID_HEADER_NAME'
}

const keyValidatorRegExp = (validator: KeyValidators) => {
  const expressions: Record<keyof typeof KeyValidators, RegExp> = {
    IS_VALID_FIELD_NAME: isValidFieldName,
    NO_WHITE_SPACE: noWhitespace,
    IS_VALID_HEADER_NAME: isValidHeaderName
  };

  return expressions[validator];
};
type NativeTypes = string | number | boolean;

type ITableState = Array<{
  index: number;
  current: { key: string; value: NativeTypes };
}>;

type IErrorState = { index: number; type: 'key' | 'value' | 'row'; error: string };

interface KeyValueTableProps {
  isEditable: boolean;
  fieldName?: string;
  notEmpty?: boolean;
  required?: boolean;
  persistedValue: {};
  keyValidator?: KeyValidators;
  onTableValueChange: (key: Array<string>, value: {}) => void;
  onTableValueBlur?: (key: Array<string>, value: {}) => void;
  keyColumnLocked?: boolean;
}
const KeyValueTable: FC<KeyValueTableProps> = ({
  isEditable,
  fieldName,
  persistedValue,
  keyValidator = KeyValidators.IS_VALID_FIELD_NAME,
  required,
  notEmpty,
  onTableValueChange,
  onTableValueBlur,
  keyColumnLocked
}) => {
  if (!persistedValue) {
    persistedValue = {};
  }
  const fromRecord = (input: IPropertyBag): ITableState => {
    let count = -1;
    const output: ITableState = mapRecordToArray(input, (value: string, key: string) => {
      // eslint-disable-next-line no-plusplus
      count++;

      return {
        index: count,
        current: {
          key,
          value
        }
      };
    });
    if (isEditable && !output.length && (required || notEmpty)) {
      // eslint-disable-next-line no-plusplus
      count++;
      output.push({
        index: count,
        current: {
          key: '',
          value: ''
        }
      });
    }

    return output;
  };

  const toRecord = (table: ITableState): [IPropertyBag, Array<IErrorState>] => {
    const output: IPropertyBag = {};
    const errors: Array<IErrorState> = [];
    let hasUndefinedKey = false;
    table.forEach((row, index) => {
      let keyErrors = false;
      if (!row.current.key.length) {
        keyErrors = true;
        errors.push({ index, type: 'key', error: 'Key is Required' });
      } else {
        if (!keyValidatorRegExp(keyValidator).test(row.current.key)) {
          keyErrors = true;
          errors.push({ index, type: 'key', error: 'Key is not valid' });
        }
        const found = table.filter(searchRow => searchRow.current.key === row.current.key);
        if (found.length > 1) {
          const persistedValueKey = Object.keys(persistedValue)[row.index];
          if (persistedValueKey === row.current.key) {
            // console.log('problem in the other');
          } else {
            keyErrors = true;
            errors.push({ index, type: 'key', error: `Key already used. ${row.current.key}` });
          }
        }
        if (!row.current.value) {
          errors.push({ index, type: 'value', error: `Value is Required ${row.current.value}` });
        }
      }
      if (hasUndefinedKey) {
        errors.push({ index, type: 'value', error: `Multiple floating keys` });
        errors.push({ index, type: 'key', error: `Multiple floating keys` });
      } else if (!keyErrors) {
        output[row.current.key] = row.current.value;
      } else {
        const persistedValueKey = Object.keys(persistedValue)[row.index];
        // not original. but from props?
        if (persistedValueKey) {
          output[persistedValueKey] = row.current.value;
        } else {
          errors.push({ index, type: 'value', error: `A Key is required to add a value` });
          hasUndefinedKey = true;
          // output['new'] = row.current.value;
        }
      }
    });

    return [output, errors];
  };
  const [errors, setErrors] = useState<Array<IErrorState>>([]);
  const [rowArray, setRowArrayState] = useState(fromRecord(persistedValue));

  const setRowArray = (newState: ITableState, isBlur = false) => {
    setRowArrayState(newState);
    const [info, validationErrors] = toRecord(newState);
    setErrors(validationErrors);
    if (isBlur && onTableValueBlur) {
      onTableValueBlur([], info);
    } else {
      onTableValueChange([], info);
    }
  };

  const addRow = () => {
    const newArray = cloneDeep(rowArray);
    newArray.push({
      index: rowArray.length,
      current: {
        key: '',
        value: ''
      }
    });
    setRowArray(newArray, true);
  };

  const removeRow = (index: number) => {
    const newArray: ITableState = [];
    let count = 0;
    rowArray.forEach((item, itemIndex) => {
      if (index !== itemIndex) {
        if (itemIndex > index) {
          item.index = count;
        }
        // eslint-disable-next-line no-plusplus
        count++;
        newArray.push(item);
      }
    });
    setRowArray(newArray, true);
  };

  const EditRow = (index: number, key: string, value: NativeTypes, isBlur = false) => {
    const newArray = cloneDeep(rowArray);
    newArray[index].current = { key, value };
    setRowArray(newArray, isBlur);
  };

  return (
    <Box>
      {fieldName && (
        <H4 color="primaryText" fontWeight="bold" mb="xs" style={{ textTransform: 'capitalize' }}>
          {fieldName}
        </H4>
      )}
      <GridLayout gridTemplateColumns="3fr 3fr 1fr" gridColumnGap="none">
        <Box p="xs" style={{ border: '1px solid lightgrey', borderBottom: 'none', background: '#e7e7e7' }}>
          <H4 color="secondary" fontWeight="semiBold">
            KEY
          </H4>
        </Box>
        <Box p="xs" style={{ border: '1px solid lightgrey', borderBottom: 'none', background: '#e7e7e7' }}>
          <H4 color="secondary" fontWeight="semiBold">
            VALUE
          </H4>
        </Box>
      </GridLayout>
      {rowArray.map(row => (
        <Row
          isEditable={isEditable}
          key={`index_${row.index}`}
          fieldName={row.current.key}
          fieldValue={row.current.value}
          nameError={errors.find(error => error.index === row.index && error.type === 'key')?.error}
          valueError={errors.find(error => error.index === row.index && error.type === 'value')?.error}
          onRemove={() => {
            removeRow(row.index);
          }}
          onRowEdit={(key, value) => EditRow(row.index, key, value)}
          onRowBlur={(key, value) => EditRow(row.index, key, value, true)}
          // keyValidator={keyValidator}
          keyColumnLocked={keyColumnLocked}
        />
      ))}
      {isEditable && !keyColumnLocked && (
        <Button
          disabled={errors.find(error => error.type === 'key') !== undefined}
          onClick={addRow}
          endIcon={<Icon src="add" />}
        >
          Add
        </Button>
      )}
    </Box>
  );
};

export default KeyValueTable;

interface RowProps {
  isEditable: boolean;
  fieldName: string;
  fieldValue: NativeTypes;
  nameError?: string;
  valueError?: string;
  onRemove: () => void;
  onRowEdit: (key: string, value: NativeTypes) => void;
  onRowBlur?: (key: string, value: NativeTypes) => void;
  keyColumnLocked?: boolean;
  // keyValidator: KeyValidators;
}
const Row: FC<RowProps> = ({
  isEditable,
  fieldName,
  fieldValue,
  nameError,
  valueError,
  onRemove,
  onRowEdit,
  onRowBlur,
  keyColumnLocked
}) => (
  <GridLayout
    gridTemplateColumns="3fr 3fr 1fr"
    gridColumnGap="none"
    css={{
      ':hover': {
        '#delete-row': {
          opacity: 1
        }
      }
    }}
  >
    <Box p="xs" width="full" style={{ border: '1px solid lightGrey' }}>
      <RowCell
        key="name-field"
        value={fieldName}
        error={nameError}
        isEditable={isEditable && !keyColumnLocked}
        onChange={value => {
          onRowEdit(value, fieldValue);
        }}
        onBlur={value => {
          if (onRowBlur) {
            onRowBlur(value, fieldValue);
          }
        }}
      />
    </Box>
    <Box p="xs" width="full" style={{ border: '1px solid lightGrey', borderLeft: 'none' }}>
      <RowCell
        key="value-field"
        value={fieldValue}
        error={valueError}
        isEditable={isEditable}
        onChange={value => {
          onRowEdit(fieldName, value);
        }}
        onBlur={value => {
          if (onRowBlur) {
            onRowBlur(fieldName, fieldValue);
          }
        }}
      />
    </Box>
    {isEditable && !keyColumnLocked && (
      <Icon
        key="delete-row"
        src="deleteOutline"
        color="secondary"
        stroke="transparent"
        // opacity="none"
        style={{
          cursor: 'pointer',
          justifySelf: 'center',
          alignSelf: 'center',
          transition: 'opacity .2s ease-in-out'
        }}
        onClick={() => onRemove()}
      />
    )}
  </GridLayout>
);

interface RowCellProps {
  isEditable: boolean;
  value: NativeTypes;
  error?: string;
  placeholder?: string;
  onChange: (val: string) => void;
  onBlur?: (val: string) => void;
}
const RowCell: FC<RowCellProps> = ({ isEditable, value, onChange, placeholder = '', error, onBlur }) => (
  <Input
    key="myRowCell"
    fullWidth
    type="text"
    error={error !== undefined}
    label={error || undefined}
    onChange={e => {
      onChange(e.target.value);
    }}
    onBlur={e => {
      if (onBlur) {
        onBlur(e.target.value);
      }
    }}
    value={value}
    disabled={!isEditable}
    placeholder={placeholder}
    autoComplete="off"
    InputProps={{
      onKeyPress: e => {
        if (e.key === 'Enter' && onBlur) {
          onBlur(e.target.value);
        }
      }
    }}
  />
);
